import { createFileRoute } from '@tanstack/react-router';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';

import { BrandLogo, useBranding } from '@/shared/branding';

export const Route = createFileRoute('/owner-qr-poster')({
  component: OwnerQrPoster,
});

/// Printable A4 poster pointing owners at this deployment's public form. The QR
/// encodes the site the poster is opened on, so every client's deployment
/// prints its own poster — no per-client assets to regenerate.
function OwnerQrPoster() {
  const branding = useBranding();
  const submitUrl = `${window.location.origin}/submit`;
  const [qrSvg, setQrSvg] = useState('');

  useEffect(() => {
    let cancelled = false;
    void QRCode.toString(submitUrl, { type: 'svg', margin: 1, color: { dark: '#111111' } }).then(
      (svg) => {
        if (!cancelled) setQrSvg(svg);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [submitUrl]);

  useEffect(() => {
    const previous = document.title;
    document.title = `${branding.shortName.en} — Owner Delegation QR`;
    return () => {
      document.title = previous;
    };
  }, [branding.shortName.en]);

  const download = (href: string, extension: 'png' | 'svg') => {
    const link = document.createElement('a');
    link.href = href;
    link.download = `owner-qr-${window.location.hostname}.${extension}`;
    link.click();
  };
  const downloadPng = () =>
    void QRCode.toDataURL(submitUrl, { width: 2048, margin: 1 }).then((url) =>
      download(url, 'png'),
    );
  const downloadSvg = () => {
    const url = URL.createObjectURL(new Blob([qrSvg], { type: 'image/svg+xml' }));
    download(url, 'svg');
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  };

  return (
    <div className="qrp" dir="ltr">
      <style>{POSTER_CSS}</style>
      <div className="bar">
        <button type="button" className="print-btn" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
        <p className="hint">
          Opens the browser print dialog. Choose A4 and &quot;Save as PDF&quot; or send to a
          printer.
        </p>
        <div className="downloads">
          <button type="button" onClick={downloadPng} disabled={!qrSvg}>
            Download QR (PNG)
          </button>
          <button type="button" onClick={downloadSvg} disabled={!qrSvg}>
            Download QR (SVG)
          </button>
        </div>
      </div>

      <div
        className="poster"
        role="img"
        aria-label={`${branding.name.en} delegation request QR poster`}
      >
        <div className="band">
          <BrandLogo className="brand-logo" />
          <div className="sys">
            Delegation
            <br />
            System
          </div>
        </div>

        <div className="body">
          <h1 className="ar-title">بوابة تفويض المُلّاك</h1>
          <p className="ar-sub">امسح الرمز لتقديم طلب تفويض للدخول إلى وحدتك</p>
          <p className="en-sub">Scan to submit a delegation request for your unit</p>

          {/* Generated locally by the qrcode library from our own URL. */}
          <div className="qr-card" dangerouslySetInnerHTML={{ __html: qrSvg }} />

          <div className="url">{window.location.host}</div>
          <p className="scan-note">افتح الكاميرا ووجّهها نحو الرمز</p>
        </div>

        <div className="foot">
          <div className="steps">
            <span>
              <b>١</b> امسح الرمز
            </span>
            <span>
              <b>٢</b> اختر وحدتك
            </span>
            <span>
              <b>٣</b> أضف المفوّضين
            </span>
          </div>
          <span className="sss">
            Powered by <b>SSS</b>
          </span>
        </div>
      </div>
    </div>
  );
}

/// Self-contained print styles, scoped under `.qrp` (the page-level rules only
/// apply while this route is mounted).
const POSTER_CSS = `
  .qrp {
    --ink: #14103a; --indigo: #2a1d6e; --indigo-2: #3a2a8f; --violet: #6d5ef0;
    --slate: #5b5570; --hair: #e7e4f2; --desk: #eceaf4;
    background: var(--desk); color: var(--ink);
    font-family: "Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    display: flex; flex-direction: column; align-items: center; gap: 18px;
    padding: 28px 16px 48px; min-height: 100vh; box-sizing: border-box;
  }
  .qrp * { box-sizing: border-box; }
  .qrp .bar { display: flex; flex-direction: column; align-items: center; gap: 8px; }
  .qrp .print-btn {
    appearance: none; border: 0; cursor: pointer; font: inherit; font-weight: 650;
    color: #fff; background: linear-gradient(135deg, var(--violet), var(--indigo-2));
    padding: 11px 22px; border-radius: 999px; box-shadow: 0 6px 18px rgba(109,94,240,.35);
  }
  .qrp .print-btn:focus-visible { outline: 3px solid var(--violet); outline-offset: 2px; }
  .qrp .hint { max-width: 22rem; text-align: center; line-height: 1.5; color: #4a4560; font-size: 13px; margin: 0; }
  .qrp .downloads { display: flex; gap: 8px; }
  .qrp .downloads button {
    appearance: none; cursor: pointer; font: inherit; font-size: 13px; font-weight: 600;
    color: var(--indigo); background: #fff; border: 1px solid var(--hair);
    padding: 7px 14px; border-radius: 999px;
  }
  .qrp .downloads button:disabled { opacity: .5; cursor: default; }

  .qrp .poster {
    width: 210mm; max-width: 100%; aspect-ratio: 210 / 297;
    background: #fff; color: var(--ink); border-radius: 16px; overflow: hidden;
    box-shadow: 0 24px 60px rgba(20,16,58,.22); display: flex; flex-direction: column;
  }
  .qrp .band {
    background: radial-gradient(120% 140% at 100% 0%, var(--indigo-2), var(--indigo) 60%, var(--ink));
    color: #fff; padding: 30px 44px 26px; display: flex; align-items: center;
    justify-content: space-between; gap: 16px;
  }
  .qrp .brand-logo { height: auto; width: auto; max-height: 64px; max-width: 240px;
    object-fit: contain; filter: brightness(0) invert(1); }
  .qrp .sys { text-align: right; font-size: 12px; letter-spacing: 2px; opacity: .85; text-transform: uppercase; }

  .qrp .body { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center; padding: 20px 40px 8px; gap: 6px; }
  .qrp .ar-title { direction: rtl; font-size: 40px; font-weight: 800; color: var(--indigo); text-wrap: balance; margin: 0; }
  .qrp .ar-sub { direction: rtl; font-size: 19px; color: var(--slate); margin: 2px 0 0; line-height: 1.5; }
  .qrp .en-sub { font-size: 15px; color: var(--slate); margin: 4px 0 0; }

  .qrp .qr-card { margin: 22px 0 10px; padding: 20px; background: #fff; border: 1px solid var(--hair);
    border-radius: 18px; box-shadow: 0 10px 30px rgba(20,16,58,.10); }
  .qrp .qr-card svg { width: 260px; height: 260px; display: block; }
  .qrp .url { font-family: "Cascadia Code", "SF Mono", Consolas, monospace; font-size: 16px; font-weight: 600; letter-spacing: .3px; }
  .qrp .scan-note { direction: rtl; font-size: 14px; color: var(--slate); margin: 2px 0 0; }

  .qrp .foot { border-top: 1px solid var(--hair); padding: 16px 44px 24px; display: flex;
    align-items: center; justify-content: space-between; font-size: 12px; color: var(--slate); }
  .qrp .foot .sss b { color: var(--indigo); font-weight: 800; }
  .qrp .steps { direction: rtl; display: flex; gap: 18px; }
  .qrp .steps b { color: var(--indigo); }

  @media (max-width: 640px) {
    .qrp .band { padding: 22px; } .qrp .ar-title { font-size: 30px; }
    .qrp .qr-card svg { width: 210px; height: 210px; }
    .qrp .foot { flex-direction: column; gap: 8px; text-align: center; }
  }
  @media print {
    @page { size: A4 portrait; margin: 0; }
    html, body { background: #fff !important; }
    .qrp { background: #fff; padding: 0; gap: 0; min-height: 0; }
    .qrp .bar { display: none !important; }
    .qrp .poster { box-shadow: none; border-radius: 0; width: 210mm; height: 297mm; aspect-ratio: auto;
      -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
`;
